import { useEffect, useState, useCallback, useMemo } from 'react';
import { Country } from 'api';
import { ProductWithGrouping } from './types';
import { GridCellEditCommitParams } from '@mui/x-data-grid';

// Custom hook for handling products data
export const useProductsData = (activeCountry: Country | undefined) => {
  const [data, setData] = useState<ProductWithGrouping[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [denseView, setDenseView] = useState<boolean>(true);

  // Transform products data for grouped view
  const groupedProducts = useMemo(() => {
    if (!data.length) return [];

    // Group products by category
    const categories = new Map<string, ProductWithGrouping[]>();
    
    // Extract regular products (not category rows)
    const regularProducts = data.filter(product => !product.__isCategory);
    
    regularProducts.forEach(product => {
      const category = product.category || 'Uncategorized';
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category)?.push(product);
    });

    // Create flat array with category headers and products
    const result: ProductWithGrouping[] = [];
    
    categories.forEach((products, categoryName) => {
      // Add category row (parent)
      const categoryId = `category-${categoryName}`;
      const isCategoryExpanded = expandedCategories.has(categoryId);
      
      const categoryRow: ProductWithGrouping = {
        id: categoryId,
        category: categoryName,
        heading: '',
        subHeading: '',
        calculationReady: '',
        calculationDiscard: '',
        __isCategory: true,
        __categoryName: categoryName,
        __categoryExpanded: isCategoryExpanded,
      };
      
      result.push(categoryRow);
      
      // Add product rows with visibility based on category expansion
      products.forEach(product => {
        result.push({
          ...product,
          __hidden: !isCategoryExpanded,
          __categoryName: categoryName
        });
      });
    });
    
    return result;
  }, [data, expandedCategories]);

  const toggleCategoryExpansion = useCallback((categoryId: string) => {
    setExpandedCategories(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(categoryId)) {
        newExpanded.delete(categoryId);
      } else {
        newExpanded.add(categoryId);
      }
      return newExpanded;
    });
  }, []);

  // Expand all categories
  const expandAllCategories = useCallback(() => {
    const allCategoryIds = groupedProducts
      .filter(product => product.__isCategory)
      .map(category => category.id as string);
    
    setExpandedCategories(new Set(allCategoryIds));
  }, [groupedProducts]);

  // Collapse all categories
  const collapseAllCategories = useCallback(() => {
    setExpandedCategories(new Set());
  }, []);

  // Toggle dense/comfortable view
  const toggleDenseView = useCallback((isDense: boolean) => {
    setDenseView(isDense);
  }, []);

  useEffect(() => {
    if (activeCountry) {
      setData(activeCountry.products || []);
      setIsLoading(false);
    }
  }, [activeCountry]);

  const handleCellEditCommit = useCallback(({ id, field, value }: GridCellEditCommitParams) => {
    // Skip editing category rows
    if (typeof id === 'string' && id.startsWith('category-')) {
      return;
    }
    
    setData(prevData => {
      const newData = [...prevData];
      const index = newData.findIndex(({ id: productId }) => productId === id);
      if (index !== -1) {
        newData[index] = { ...newData[index], [field]: value };
      }
      return newData;
    });
  }, []);

  const handleAddProduct = useCallback(() => {
    // Find default category - use the first category if available
    let defaultCategory = 'Uncategorized';
    
    const categories = data
      .filter(p => !p.__isCategory)
      .map(p => p.category);
      
    if (categories.length > 0) {
      defaultCategory = categories[0] || 'Uncategorized';
    }
    
    const newRow: ProductWithGrouping = { 
      id: `${Date.now()}`, 
      category: defaultCategory,
      heading: '',
      subHeading: '',
      calculationReady: '',
      calculationDiscard: ''
    };
    
    // Add the new product
    setData(prevData => [...prevData, newRow]);
    
    // Make sure the category is expanded
    setExpandedCategories(prev => {
      const newExpanded = new Set(prev);
      newExpanded.add(`category-${defaultCategory}`);
      return newExpanded;
    });
  }, [data]);

  const handleDeleteProduct = useCallback((id: string | number) => {
    // Skip deletion if it's a category row
    if (typeof id === 'string' && id.startsWith('category-')) {
      return {};
    }
    
    setData(prevData => prevData.filter(({ id: productId }) => productId !== id));
    return {};
  }, []);

  return useMemo(() => ({
    data,
    groupedProducts,
    isLoading,
    denseView,
    handleCellEditCommit,
    handleAddProduct,
    handleDeleteProduct,
    toggleCategoryExpansion,
    expandAllCategories,
    collapseAllCategories,
    toggleDenseView,
    setData
  }), [
    data,
    groupedProducts,
    isLoading,
    denseView,
    handleCellEditCommit,
    handleAddProduct,
    handleDeleteProduct,
    toggleCategoryExpansion,
    expandAllCategories,
    collapseAllCategories,
    toggleDenseView,
    setData
  ]);
};
